import { filter, isEqual } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { OptionType } from '../SelectMenu/types';
import { SelectMenuFilter, SelectMenuFilterProps } from './SelectMenuFilter';

export type selectMenuFilterFn<T> = (
  items: OptionType<T>[],
  filterText: string,
) => OptionType<T>[] | Promise<OptionType<T>[]>;

export function useSelectMenuFilter<T>(
  options: OptionType<T>[],
  props: Pick<SelectMenuFilterProps, 'compact' | 'placeholder'>,
  filterFn: selectMenuFilterFn<T> = substringFilterByLabel,
) {
  const filterRef = useRef<HTMLInputElement | null>(null);
  const [filterValue, setFilterValue] = useState('');
  const [filteredItems, setFilteredItems] = useState(options);

  useEffect(() => {
    // reset when no filter value is set
    if (!filterValue) return setFilteredItems(options);
    const updateFilteredItems = (results: OptionType<T>[]) =>
      setFilteredItems(items => (isEqual(items, results) ? items : results));
    // result can be a promise or an array of items
    const result = filterFn(options, filterValue);
    if (result instanceof Promise) {
      // if the result is a promise, wait for it to resolve then update the filtered items
      result.then(updateFilteredItems);
    } else {
      // otherwise update the filtered items immediately
      updateFilteredItems(result);
    }
  }, [options, filterFn, filterValue]);

  const filterProps: SelectMenuFilterProps = {
    ...props,
    value: filterValue,
    onChange: setFilterValue,
  };
  return {
    filteredItems,
    filterProps,
    filterValue,
    Filter: SelectMenuFilter,
    filterRef,
  };
}

const substringFilterByLabel = <T>(items: OptionType<T>[], filterText: string): OptionType<T>[] =>
  filter(items, item => item.label.toLowerCase().includes(filterText.toLowerCase()));
